import { useMemo } from 'react';
import { useServicePlanSummary } from '@features/service-plans';
import type { ConfiguredAddOn, ConfiguredService } from '@features/service-plans';
import type { PricedBasket } from '../types/basketPricing.types';
import { mergePricedLines } from '../utils/pricedLines';
import {
    buildAddOnLineViews,
    buildServiceLineViews,
    buildTotalsView,
    type QuoteLineView,
    type QuoteServiceLineView,
    type QuoteSummaryTotalsView,
} from '../utils/quoteSummaryLines';

interface QuoteSummaryLines {
    serviceLines: QuoteServiceLineView[];
    addOnLines: QuoteLineView[];
    totals: QuoteSummaryTotalsView;
}

export const useQuoteSummaryLines = (
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[],
    pricing: PricedBasket | undefined,
    currency: string,
): QuoteSummaryLines => {
    const merged = useMemo(
        () => mergePricedLines(services, addOns, pricing),
        [services, addOns, pricing],
    );
    const { summary } = useServicePlanSummary(merged.services, merged.addOns, pricing);

    return useMemo(
        () => ({
            serviceLines: buildServiceLineViews(merged, pricing, currency),
            addOnLines: buildAddOnLineViews(merged, pricing, currency),
            totals: buildTotalsView(summary, currency),
        }),
        [merged, pricing, currency, summary],
    );
};
